// Utility to patch existing payroll records with missing calculation_metadata
import { supabase } from '../services/supabase';
import moment from 'moment';

/**
 * Patches existing payroll records to add calculation_metadata
 * This calculates working_days from timesheet_daily_records
 */
export const patchPayrollMetadata = async (periodId, companyId) => {
  try {
    console.log('🔧 Starting to patch payroll metadata for period:', periodId);

    // Get all payroll records for this period
    const { data: payrollRecords, error: fetchError } = await supabase
      .from('payroll_records')
      .select(`
        id,
        employee_id,
        calculation_metadata,
        regular_hours,
        overtime_hours,
        holiday_hours
      `)
      .eq('period_id', periodId)
      .eq('company_id', companyId);

    if (fetchError) throw fetchError;

    if (!payrollRecords || payrollRecords.length === 0) {
      console.log('❌ No payroll records found for this period');
      return { success: false, message: 'No payroll records found' };
    }

    console.log(`📊 Found ${payrollRecords.length} payroll records to patch`);

    // Get the period dates
    const { data: period, error: periodError } = await supabase
      .from('payroll_periods')
      .select('start_date, end_date')
      .eq('id', periodId)
      .single();

    if (periodError) throw periodError;

    console.log(`📅 Period: ${period.start_date} to ${period.end_date}`);

    // Process each payroll record
    const updates = [];

    for (const record of payrollRecords) {
      // Get timesheet records for this employee in the period
      const { data: timesheetRecords, error: timesheetError } = await supabase
        .from('timesheet_daily_records')
        .select('date, total_hours, status, leave_type')
        .eq('employee_id', record.employee_id)
        .eq('company_id', companyId)
        .gte('date', period.start_date)
        .lte('date', period.end_date);

      if (timesheetError) {
        console.error('Error fetching timesheet for employee:', record.employee_id, timesheetError);
        continue;
      }

      // Calculate working days from unique dates where employee was present
      const uniqueDates = new Set();
      let totalRegularHours = 0;
      let totalPHHours = 0;

      (timesheetRecords || []).forEach(ts => {
        if (ts.status === 'present' || ts.total_hours > 0) {
          const date = moment(ts.date);
          const isSunday = date.day() === 0;
          const isPH = ts.leave_type === 'PH';

          const hours = parseFloat(ts.total_hours) || 0;

          if (isPH || isSunday) {
            totalPHHours += hours;
          } else {
            totalRegularHours += hours;
          }

          uniqueDates.add(ts.date);
        }
      });

      const workingDays = uniqueDates.size;

      // Build metadata object
      const metadata = {
        method: 'patched_from_timesheet',
        working_days: workingDays,
        total_records: timesheetRecords?.length || 0,
        unique_dates: Array.from(uniqueDates).sort(),
        calculated_regular_hours: totalRegularHours,
        calculated_ph_hours: totalPHHours,
        patched_at: new Date().toISOString()
      };

      console.log(`✅ Employee ${record.employee_id}: ${workingDays} working days`);

      updates.push({
        id: record.id,
        calculation_metadata: JSON.stringify(metadata)
      });
    }

    // Update all records
    console.log(`💾 Updating ${updates.length} payroll records...`);

    for (const update of updates) {
      const { error: updateError } = await supabase
        .from('payroll_records')
        .update({ calculation_metadata: update.calculation_metadata })
        .eq('id', update.id);

      if (updateError) {
        console.error('Error updating record:', update.id, updateError);
      }
    }

    console.log('✅ Successfully patched all payroll records!');

    return {
      success: true,
      message: `Successfully updated ${updates.length} records with calculation_metadata`,
      recordsUpdated: updates.length
    };

  } catch (error) {
    console.error('❌ Error patching payroll metadata:', error);
    return {
      success: false,
      message: error.message,
      error
    };
  }
};

export default patchPayrollMetadata;
